import React, { useState, useMemo } from 'react';
import './ProductProfitabilityTable.css';

const ProductProfitabilityTable = ({ data, onExport }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filterText, setFilterText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(value);
  };
  
  // Format percentage
  const formatPercentage = (value) => {
    return `${value.toFixed(1)}%`;
  };
  
  // Sorting logic
  const sortedData = useMemo(() => {
    let sortableData = [...data];
    
    if (sortConfig.key) {
      sortableData.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        
        // Handle string comparison for product names
        if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }
        
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    
    return sortableData;
  }, [data, sortConfig]);
  
  // Filtering logic
  const filteredData = useMemo(() => {
    if (!filterText) return sortedData;
    
    return sortedData.filter(item =>
      item.productName.toLowerCase().includes(filterText.toLowerCase())
    );
  }, [sortedData, filterText]);
  
  // Pagination logic
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredData, currentPage, itemsPerPage]);
  
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  
  // Handle sorting
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };
  
  // Get sort indicator
  const getSortIndicator = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return '↕️';
    }
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };
  
  // Handle pagination
  const goToPage = (page) => {
    setCurrentPage(page);
  };
  
  const goToPreviousPage = () => {
    setCurrentPage(Math.max(1, currentPage - 1));
  };
  
  const goToNextPage = () => {
    setCurrentPage(Math.min(totalPages, currentPage + 1));
  };
  
  // Handle filter change
  const handleFilterChange = (e) => {
    setFilterText(e.target.value);
    setCurrentPage(1); // Reset to first page when filtering
  };
  
  // Clear filter
  const clearFilter = () => {
    setFilterText('');
    setCurrentPage(1);
  };

  return (
    <div className="profitability-table-container">
      {/* Table Controls */}
      <div className="table-controls">
        <div className="filter-section">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search products..."
              value={filterText}
              onChange={handleFilterChange}
              className="search-input"
            />
            {filterText && (
              <button
                className="clear-filter-btn"
                onClick={clearFilter}
                title="Clear search"
              >
                ✕
              </button>
            )}
          </div>
          <div className="result-count">
            Showing {paginatedData.length} of {filteredData.length} products
          </div>
        </div>
        
        <div className="table-actions">
          <button
            className="btn-secondary export-btn"
            onClick={onExport}
            title="Export to CSV"
          >
            📊 Export CSV
          </button>
        </div>
      </div>
      
      {/* Table */}
      <div className="table-wrapper">
        <table className="profitability-table">
          <thead>
            <tr>
              <th
                className="sortable-header"
                onClick={() => requestSort('productName')}
              >
                Product Name {getSortIndicator('productName')}
              </th>
              <th
                className="sortable-header"
                onClick={() => requestSort('revenue')}
              >
                Revenue {getSortIndicator('revenue')}
              </th>
              <th
                className="sortable-header"
                onClick={() => requestSort('cost')}
              >
                Cost {getSortIndicator('cost')}
              </th>
              <th
                className="sortable-header"
                onClick={() => requestSort('profitMargin')}
              >
                Profit Margin {getSortIndicator('profitMargin')}
              </th>
              <th
                className="sortable-header"
                onClick={() => requestSort('unitsSold')}
              >
                Units Sold {getSortIndicator('unitsSold')}
              </th>
              <th
                className="sortable-header"
                onClick={() => requestSort('profitPerUnit')}
              >
                Profit per Unit {getSortIndicator('profitPerUnit')}
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.length > 0 ? (
              paginatedData.map((product) => (
                <tr key={product.id} className="table-row">
                  <td className="product-name">{product.productName}</td>
                  <td className="revenue">{formatCurrency(product.revenue)}</td>
                  <td className="cost">{formatCurrency(product.cost)}</td>
                  <td className="profit-margin">
                    <span className={`margin-indicator ${product.profitMargin >= 40 ? 'high' : product.profitMargin >= 25 ? 'medium' : 'low'}`}>
                      {formatPercentage(product.profitMargin)}
                    </span>
                  </td>
                  <td className="units-sold">{product.unitsSold.toLocaleString()}</td>
                  <td className="profit-per-unit">{formatCurrency(product.profitPerUnit)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="no-data">
                  {filterText ? 'No products match your search criteria.' : 'No data available.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <div className="pagination-info">
            Page {currentPage} of {totalPages}
          </div>
          <div className="pagination-controls">
            <button
              className="pagination-btn"
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            
            {/* Page numbers */}
            <div className="page-numbers">
              {[...Array(totalPages)].map((_, index) => {
                const page = index + 1;
                
                // Show first page, last page, current page, and pages around current page
                if (
                  page === 1 ||
                  page === totalPages ||
                  (page >= currentPage - 1 && page <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={page}
                      className={`page-btn ${currentPage === page ? 'active' : ''}`}
                      onClick={() => goToPage(page)}
                    >
                      {page}
                    </button>
                  );
                }
                
                // Show ellipsis
                if (page === currentPage - 2 || page === currentPage + 2) {
                  return <span key={page} className="page-ellipsis">...</span>;
                }
                
                return null;
              })}
            </div>
            
            <button
              className="pagination-btn"
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductProfitabilityTable;